import { useRecoilValue } from 'recoil';
import { DvfField, DvfGeometry } from '~/domain/dvf/dvfField';
import {
    computeDvfUncertainty,
    dvfGeometriesMatch,
} from '~/domain/dvf/dvfUncertainty';
import { isDeformableRegistration } from '~/domain/patient/deformableRegistration';
import { Patient } from '~/domain/patient/patient';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { toDvfField } from '~/hooks/workflows/dvf/deformableRegistrationField';
import { deformationUncertaintySourcesState } from '~/state/atoms/deformationDisplayAtoms';

export interface DeformationUncertainty {
    readonly values: Float32Array;
    readonly maxMm: number;
    readonly geometry: DvfGeometry;
    readonly sampleCount: number;
    // The frame the samples deform from; the spread means nothing over another.
    readonly frameOfReferenceUid: string;
}

const cache = new WeakMap<
    Patient,
    { key: string; value: DeformationUncertainty | null }
>();

export function deformationUncertaintyFor(
    patient: Patient,
    registrationSopInstanceUids: readonly string[]
): DeformationUncertainty | null {
    if (registrationSopInstanceUids.length < 2) {
        return null;
    }
    const key = registrationSopInstanceUids.join('|');
    const cached = cache.get(patient);
    if (cached?.key === key) {
        return cached.value;
    }
    const fields: DvfField[] = [];
    const frames = new Set<string>();
    for (const uid of registrationSopInstanceUids) {
        const registration = patient.getRegistrationWithSopInstanceUid(uid);
        const field =
            registration && isDeformableRegistration(registration)
                ? toDvfField(registration)
                : undefined;
        if (!field || !registration) {
            cache.set(patient, { key, value: null });
            return null;
        }
        frames.add(registration.sourceFrameOfReferenceUid);
        fields.push(field);
    }
    if (!dvfGeometriesMatch(fields) || frames.size !== 1) {
        cache.set(patient, { key, value: null });
        return null;
    }
    const { values, max, geometry } = computeDvfUncertainty(fields);
    const value = {
        values,
        maxMm: max,
        geometry,
        sampleCount: fields.length,
        frameOfReferenceUid: [...frames][0],
    };
    cache.set(patient, { key, value });
    return value;
}

export function useDeformationUncertainty(): DeformationUncertainty | null {
    const patient = useSelectedPatient();
    const sources = useRecoilValue(deformationUncertaintySourcesState);
    return patient ? deformationUncertaintyFor(patient, sources) : null;
}
